import { Badge } from "@comps/ui/badge";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@comps/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@comps/ui/popover";
import Field, { FieldChoice, fieldChoiceByNames, FieldType, TYPE_ITEMS_CHOICE_OPTIONS } from "@dto/fields";
import { TW_FilterEditorRecordSelect, TW_SelectionRecordBadge, TW_FilterEditorRecordSelectTriggerDisabled, TW_FilterEditorRecordSelectTrigger, TW_SelectionRecordBadgeMore, TW_ChoiceEditionPopoverContent, TW_ChoiceScrollArea, TW_ChoiceItemSelected, TW_ChoiceItem, TW_ChoiceItemText, TW_CommonCommandInput, getBadgeStyle, TW_SelectionRecordBadgeDisabled, TW_SelectionRecordBadgeMoreDisabled } from "@utils/twClasses";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useModelStore } from "src/store";


type ChoiceSelectProps = {
    field: Field | null;
    value: any;
    disabled: boolean;
    onSelectionChange: (value: any) => void;
}

export function ChoiceSelect({ field, value, disabled, onSelectionChange }: ChoiceSelectProps) {

    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const wsColor = useModelStore(state => state.wsColor);

    const values = value ? value as string[] : [];
    const firstValue = (values.length > 0) ? values[0] : null;
    const firstValueStr = firstValue ? firstValue : null;

    const nextValues = (values.length > 1) ? values.filter(v => v !== firstValue) : [];
    const nextTooltip = nextValues.join(", ");

    const badgeStyle = useMemo(() => {
        return getBadgeStyle(wsColor);
    }, [wsColor]);

    const options = field?.typeItems?.get(TYPE_ITEMS_CHOICE_OPTIONS) ?? [];
    return (
        <Popover open={isDialogOpen} onOpenChange={setIsDialogOpen}>
            <PopoverTrigger disabled={disabled} className={(disabled ? " " + TW_FilterEditorRecordSelectTriggerDisabled : TW_FilterEditorRecordSelectTrigger)}>
                <div className={TW_FilterEditorRecordSelect}>
                    {firstValueStr && <Badge key={firstValue} className={disabled ? TW_SelectionRecordBadgeDisabled : TW_SelectionRecordBadge} style={badgeStyle}>{firstValueStr}</Badge>}
                    {nextValues.length > 0 && <Badge key={nextValues[0]} className={disabled ? TW_SelectionRecordBadgeMoreDisabled : TW_SelectionRecordBadgeMore} tooltip={nextTooltip} style={badgeStyle}>{"+" + nextValues.length}</Badge>}
                </div>
            </PopoverTrigger>
            <PopoverContent className={TW_ChoiceEditionPopoverContent}>
                <ChoiceEditionForm field={field!} selectedChoices={values} options={options} updateFieldChoices={onSelectionChange} allowMultiple={true} />
            </PopoverContent>
        </Popover>
    )
}


interface ChoiceEditionFormProps {
    field: Field | null,
    selectedChoices: string[],
    options: FieldChoice[],
    allowMultiple: boolean,
    updateFieldChoices: (fieldChoices: string[]) => void
}
    
function ChoiceEditionForm({ field, selectedChoices, options, allowMultiple, updateFieldChoices }: ChoiceEditionFormProps) {

    const { t } = useTranslation();
    const wsColor = useModelStore(state => state.wsColor);
    const wsLightColor = useModelStore(state => state.wsLightColor);

    const onOptionSelected = (selectedOption: string) => {

        const wasChoosen = selectedChoices?.some(choosenItem => choosenItem === selectedOption) || false;
        const fieldChoice = options.find(option => option.name === selectedOption);

        if (!fieldChoice || !field) {
            return;
        }

        let newChoices = selectedChoices ? [...selectedChoices] : [];
        if (wasChoosen) {
            newChoices = newChoices.filter(choosenItem => choosenItem !== selectedOption);
        } else {
            if (allowMultiple || field.type === FieldType.MultiChoice) {
                newChoices.push(fieldChoice.name);
            } else {
                newChoices = [fieldChoice.name];
            }
        }

        updateFieldChoices(newChoices);
    }

    const scrollStyle = {
        scrollbarColor: wsColor + " " + wsLightColor,
    }

    const choiceByNames = fieldChoiceByNames(options);
    return (

        <Command>
            <CommandInput placeholder={t("data.choice.search.placeholder")} className={TW_CommonCommandInput} autoFocus={true} />
            <div className={TW_ChoiceScrollArea}>
                <CommandList className="h-full max-h-full w-full scrollbar-thin scrollbar-button-hidden" style={scrollStyle}>
                    <CommandEmpty>{t("data.table.noChoiceItems")}</CommandEmpty>
                    <CommandGroup>
                        {options.map((option: FieldChoice) => {
                            const choiceName = option.name;
                            const choiceColor = choiceByNames.get(choiceName)?.color;
                            const isChoosen = selectedChoices?.some(choosenItem => choosenItem === choiceName);
                            let style = undefined;
                            if (isChoosen) {
                                style = { backgroundColor: choiceColor, color: "white" };
                            } else {
                                style = { color: choiceColor };
                            }
                            return (
                                <CommandItem
                                    key={choiceName}
                                    className={`${isChoosen ? TW_ChoiceItemSelected : TW_ChoiceItem}`}
                                    style={style}
                                    onSelect={() => onOptionSelected(choiceName)}>
                                    <span className={TW_ChoiceItemText} style={style}>{choiceName}</span>
                                </CommandItem>
                            )
                        })}
                    </CommandGroup>
                </CommandList>
            </div>
        </Command>
    )
}
